import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Cms } from '@impact/data';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    arrowRightIcon,
    circleCheckmarkIcon,
    progressIcon,
} from '../../../../icons';
import { BookWorkshopFlowService } from '../../../core/book-workshop-flow.service';
import { getUTCDateString } from '../../../core/helpers';
import { WorkshopService } from '../../../core/workshop.service';
import { IBooking } from '../../../models/book-workshop.models';
import { RentalCarClass } from '../book-workshop-stepper.component';

export interface IDatePickerFormat {
    year: number;
    month: number;
    day: number;
    [key: string]: any;
}

@Component({
    selector: 'app-book-workshop-select-date',
    template: `
        <section class="flow-step__header">
            <h1 class="h4">{{ 'book_workshop.select_date_time' | translate }}</h1>
            <p class="maintain-newlines">{{ 'book_workshop.select_date_secondary_text' | translate }}</p>
        </section>

        <ng-container [ngSwitch]="isReady">
            <app-component-loader *ngSwitchCase="false"></app-component-loader>

            <form [formGroup]="form" class="book-workshop-select-date__form" (ngSubmit)="onSubmit()" *ngSwitchCase="true">
                <!-- Rental cars -->
                <ng-container *ngIf="showRentalsAvailability">
                    <h2 class="h5">{{ 'book_workshop.rental_car_text' | translate }}</h2>
                    <div class="rental-cars-navigation" *ngIf="rentals">
                        <ul class="rental-cars-navigation__list">
                            <li class="rental-cars-navigation__item">
                                <button type="button"
                                    [ngClass]="{'is-selected': selectedRental === undefined}"
                                    (click)="selectRentalCar(undefined)"
                                    class="rental-cars-navigation__item-button">
                                    <span class="rental-cars-navigation__item-text">{{'book_workshop.no_rental_car_selected_button_text' | translate}}</span>
                                    <span class="selected-icon">${circleCheckmarkIcon}</span>
                                </button>
                            </li>
                            <li *ngFor="let rentalCar of rentals" class="rental-cars-navigation__item">
                                <button
                                    class="rental-cars-navigation__item-button"
                                    type="button"
                                    [class.is-selected]="rentalCar.carClassId === selectedRental?.carClassId"
                                    (click)="selectRentalCar(rentalCar)"
                                >
                                    <img
                                        class="rental-cars-navigation__item-image"
                                        *ngIf="rentalCar.image?.src"
                                        [src]="rentalCar.image?.src | resolveMediaUrl: { format: 'webp', width: 80 }"
                                    />

                                    <span
                                        class="rental-cars-navigation__item-text"
                                        [innerText]="rentalCar.displayName"
                                    ></span>

                                    <span
                                        class="rental-cars-navigation__item-price"
                                        *ngIf="rentalCar.price"
                                        [innerText]="rentalCar.price"
                                    ></span>

                                    <span class="selected-icon">${circleCheckmarkIcon}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </ng-container>

                <div class="book-workshop-select-date__wrapper-layout">
                    <div class="book-workshop-select-date__wrapper">
                        <div class="date-picker-block__wrapper">
                            <div class="book-workshop-select-date__wrapper-header">
                                <h2 class="book-workshop-select-date__heading">{{ 'book_workshop.select_date' | translate }}</h2>
                                <p
                                    class="date-picker-block__info-text"
                                    *ngIf="!selectedRental && pageContent.datePickerRentalInformationText"
                                    [innerText]="pageContent.datePickerRentalInformationText"
                                ></p>
                                <div class="date-picker-block__legend">
                                    <div class="date-picker-block__legend-item">
                                        <span class="date-picker-block__legend-availability-indicator"></span>
                                        <div class="date-picker-block__legend-text">{{'book_workshop.available_service_dates_text' | translate}}</div>
                                    </div>
                                    <div class="date-picker-block__legend-item" *ngIf="selectedRental !== undefined">
                                        <span class="date-picker-block__legend-rentals-indicator"></span>
                                        <div class="date-picker-block__legend-text"
                                            [innerHTML]="'book_workshop.available_rental_car_text' | translate: { rentalCarName: selectedRental?.displayName }"></div>
                                    </div>
                                </div>
                            </div>

                            <ng-container [ngSwitch]="isLoadingDates || !allDates">
                                <ngb-datepicker *ngSwitchCase="false"
                                    [displayMonths]="1"
                                    [navigation]="'arrows'"
                                    [outsideDays]="'visible'"
                                    [minDate]="minDate"
                                    [maxDate]="maxDate"
                                    [ngClass]="'datepicker-block'"
                                    [showWeekNumbers]="false"
                                    [startDate]="startDate"
                                    (dateSelect)="selectDate($event, true)"
                                    [dayTemplate]="customDay">
                                </ngb-datepicker>
                                <div class="faux-date-picker-wrapper" *ngSwitchCase="true">
                                    <div class="datepicker-block"></div>
                                    <div class="date-time-picker__loading-circle">${progressIcon}</div>
                                </div>
                            </ng-container>
                        </div>
                    </div>

                    <div class="book-workshop-select-date__wrapper" *ngIf="isLoadingTimes || availableTimesSections" [appScrollToElement]="isLoadingTimes">
                        <div class="book-workshop-select-date__wrapper-header">
                            <h2 class="book-workshop-select-date__heading">{{'book_workshop.select_time' | translate}}</h2>
                            <p class="center maintain-newlines" *ngIf="bookWorkshopFlowService?.selectedDeliveryOptionInfoText$ | async as deliveryOptionInfoText">
                                {{ deliveryOptionInfoText?.optionText }}
                            </p>
                        </div>
                        <div class="time-picker-block__wrapper">
                            <div class="time-picker-block">
                                <div class="time-picker-block__header" [ngSwitch]="isLoadingTimes">
                                    <ng-container *ngSwitchCase="true">{{'book_workshop.loading_timepicker_options_text' | translate}}</ng-container>
                                    <ng-container *ngSwitchCase="false">
                                        {{selectedDateString | date: 'EEEE dd. MMMM yyyy'}}
                                    </ng-container>
                                </div>
                                <ng-container [ngSwitch]="isLoadingTimes">
                                    <div class="date-time-picker__loading-circle" *ngSwitchCase="true">${progressIcon}</div>
                                    <ng-container *ngSwitchCase="false">
                                        <div class="time-picker-block__section" *ngIf="availableTimesSections.morning.length">
                                            <h6 class="time-picker-block__section-header">{{'book_workshop.morning_text' | translate}}</h6>
                                            <ul class="time-picker-block__list">
                                                <li class="time-picker-block__list-item" *ngFor="let time of availableTimesSections.morning">
                                                    <ng-container *ngTemplateOutlet="timeButton; context: {time: time}"></ng-container>
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="time-picker-block__section" *ngIf="availableTimesSections.beforenoon.length">
                                            <h6 class="time-picker-block__section-header">{{'book_workshop.before_noon_text' | translate}}</h6>
                                            <ul class="time-picker-block__list">
                                                <li class="time-picker-block__list-item" *ngFor="let time of availableTimesSections.beforenoon">
                                                    <ng-container *ngTemplateOutlet="timeButton; context: {time: time}"></ng-container>
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="time-picker-block__section" *ngIf="availableTimesSections.afternoon.length">
                                            <h6 class="time-picker-block__section-header">{{'book_workshop.afternoon_text' | translate}}</h6>
                                            <ul class="time-picker-block__list">
                                                <li class="time-picker-block__list-item" *ngFor="let time of availableTimesSections.afternoon">
                                                    <ng-container *ngTemplateOutlet="timeButton; context: {time: time}"></ng-container>
                                                </li>
                                            </ul>
                                        </div>
                                    </ng-container>
                                </ng-container>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="book-workshop-flow__bottom">
                    <div class="book-workshop-flow__bottom-content">
                        <app-button
                            buttonClass="book-workshop-stepper-content__continue-button button--tertiary"
                            (click)="previousStep.emit()"
                            [title]="'book_workshop.back' | translate">
                        </app-button>
                        <app-button
                            buttonClass="book-workshop-stepper-content__continue-button button--primary"
                            buttonType="submit"
                            [disabled]="!canSubmit"
                            [title]="'book_workshop.continue' | translate">
                            <i class="button__icon">${arrowRightIcon}</i>
                        </app-button>
                    </div>
                </div>
            </form>

            <div class="book-workshop-select-date__no-rental-information" *ngIf="showNoRentalAvailableOverlay" [ngClass]="{'is-active': showNoRentalAvailableOverlay}">
                <div class="book-workshop-select-date__no-rental-information-content">
                    <div
                        class="book-workshop-select-date__no-rental-information-text"
                        [innerHTML]="pageContent.continueWithoutRentalText | safe: 'html'"
                    ></div>

                    <div class="book-workshop-select-date__no-rental-information-actions">
                        <button type="button" class="button--tertiary" (click)="continueWithNoRentalOnDate(false)">
                            {{ 'book_workshop.rental_modal_cancel_button_text' | translate }}
                        </button>
                        <button type="button" class="button--primary" (click)="continueWithNoRentalOnDate(true)">
                            {{ 'book_workshop.rental_modal_continue_button_text' | translate }}
                        </button>
                    </div>
                </div>
            </div>
        </ng-container>

        <!-- Templates -->
        <ng-template #customDay let-date let-currentMonth="currentMonth" let-selected="selected" let-disabled="disabled" let-focused="focused">
            <button type="button" class="datepicker-block__day"
                [ngClass]="{
                    'is-available': dateIsAvailable(date),
                    'is-selected': selectedDate?.year === date?.year && selectedDate?.month === date?.month && selectedDate?.day === date?.day,
                    'disabled': !dateIsAvailable(date),
                    'hidden': date.month !== currentMonth,
                    'focused': focused
                }">
                <span class="datepicker-block__day-text">
                    {{date.day}}
                </span>
                <span class="is-available-with-rental" *ngIf="showRentalsAvailability && dateIsAvailableWithRental(date) && selectedRental"></span>
            </button>
        </ng-template>

        <ng-template #timeButton let-time="time">
            <button type="button" [ngClass]="{'is-selected': selectedTime === time}" (click)="selectTime(time)">{{time}}</button>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookWorkshopSelectDateComponent implements OnInit, OnDestroy {

    @Input() booking: IBooking;
    @Input() rentals: RentalCarClass[];
    @Input() pageContent: Cms.BookWorkshopPage;
    @Output() nextStep = new EventEmitter();
    @Output() previousStep = new EventEmitter();

    showRentalsAvailability: boolean;
    formattedAvailableShopDates: any[];
    formattedAvailableShopDatesWithRental: any[];
    startDate: any;
    startMonth: any;
    endMonth: any;
    selectedRental: any;
    hasSelectedDate: boolean;
    selectedDate: NgbDateStruct | undefined;
    selectedDateString: string;
    selectedTime: string;
    unavailableDates: any[];
    allDates: any[];
    isLoadingDates: boolean;
    isLoadingTimes: boolean;
    availableTimes: string[];
    availableTimesSections: any;
    canSubmit: boolean;
    showNoRentalAvailableOverlay: boolean;
    isReady: boolean;

    minDate: NgbDateStruct;
    maxDate: NgbDateStruct;

    form = this.fb.group({
        date: ['', Validators.required],
        time: [{ value: '', disabled: true }, Validators.required],
    });

    private readonly unsubscribe = new Subject<void>();

    get dateControl() {
        return this.form.get('date');
    }

    get timeControl() {
        return this.form.get('time');
    }

    constructor(
        private workshopService: WorkshopService,
        public bookWorkshopFlowService: BookWorkshopFlowService,
        private fb: UntypedFormBuilder,
        private cdr: ChangeDetectorRef
    ) {
        this.isLoadingDates = true;
        this.canSubmit = false;
        this.isReady = false;
    }

    ngOnInit(): void {
        // Check if we need to get dates with rentals or not
        if (this.booking) {
            this.showRentalsAvailability = (this.booking.optionId !== '1002' && this.rentals.length !== 0) || !!this.booking.rentalCar;

            if (this.booking.rentalCar) {
                this.selectedRental = this.rentals.filter((rental) => rental.name === this.booking.rentalCarClassName)[0];
                this.selectRentalCar(this.selectedRental);

                // Get dates with rentals
                this.workshopService.getBookingDatesWithRental(this.booking, this.selectedRental.carClassId)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe((datesWithRentals: any) => {
                        this.formattedAvailableShopDates = this.constructDatePickerObjects(datesWithRentals, true);

                        this.startDate = {
                            year: this.formattedAvailableShopDates[0].year,
                            month: this.formattedAvailableShopDates[0].month,
                            day: 1
                        };

                        this.startMonth = {
                            year: this.formattedAvailableShopDates[0].year,
                            month: this.formattedAvailableShopDates[0].month
                        };

                        this.endMonth = {
                            year: this.formattedAvailableShopDates[this.formattedAvailableShopDates.length - 1].year,
                            month: this.formattedAvailableShopDates[this.formattedAvailableShopDates.length - 1].month
                        };

                        this.minDate = {
                            year: this.formattedAvailableShopDates[0].year,
                            month: this.formattedAvailableShopDates[0].month,
                            day: 1
                        };

                        // Get the last available month data
                        const endDateString = new Date(
                            this.formattedAvailableShopDates[this.formattedAvailableShopDates.length - 1].year,
                            this.formattedAvailableShopDates[this.formattedAvailableShopDates.length - 1].month,
                            0
                        );

                        const month = endDateString.getMonth() + 1;
                        const year = endDateString.getFullYear();
                        const daysInMonth = new Date(year, month, 0).getDate();

                        this.maxDate = {
                            year: this.endMonth.year,
                            month: this.endMonth.month,
                            day: daysInMonth
                        };

                        this.allDates = this.formattedAvailableShopDates;

                        if (this.booking && this.booking.date) {
                            this.hasPreselectedDate(this.booking.date);
                        }

                        this.isLoadingDates = false;
                        this.isReady = true;

                        this.cdr.markForCheck();
                    });
            } else {
                this.workshopService.getBookingDates(this.booking)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe((availableDates: any) => {
                        if (availableDates) {
                            this.formattedAvailableShopDates = this.constructDatePickerObjects(availableDates);

                            this.startDate = {
                                year: this.formattedAvailableShopDates[0].year,
                                month: this.formattedAvailableShopDates[0].month,
                                day: 1
                            };

                            this.startMonth = {
                                year: this.formattedAvailableShopDates[0].year,
                                month: this.formattedAvailableShopDates[0].month
                            };

                            this.endMonth = {
                                year: this.formattedAvailableShopDates[this.formattedAvailableShopDates.length - 1].year,
                                month: this.formattedAvailableShopDates[this.formattedAvailableShopDates.length - 1].month
                            };

                            this.minDate = {
                                year: this.formattedAvailableShopDates[0].year,
                                month: this.formattedAvailableShopDates[0].month,
                                day: 1
                            };

                            // Get the last available month data
                            const endDateString = new Date(
                                this.formattedAvailableShopDates[this.formattedAvailableShopDates.length - 1].year,
                                this.formattedAvailableShopDates[this.formattedAvailableShopDates.length - 1].month,
                                0
                            );

                            const month = endDateString.getMonth() + 1;
                            const year = endDateString.getFullYear();
                            const daysInMonth = new Date(year, month, 0).getDate();

                            this.maxDate = {
                                year: this.endMonth.year,
                                month: this.endMonth.month,
                                day: daysInMonth
                            };

                            this.allDates = this.formattedAvailableShopDates;

                            if (this.booking && this.booking.date) {
                                this.hasPreselectedDate(this.booking.date);
                            }

                            this.isLoadingDates = false;
                            this.isReady = true;

                            this.cdr.markForCheck();
                        }
                    });
            }

        } else {
            console.warn('[BN error: No booking data]');
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    // If a date is present in localstorage, we apply this date
    hasPreselectedDate(date: any) {
        const selectedDate: any = this.constructDatePickerObjects([date])[0];
        this.startDate = date;

        if (this.dateIsAvailable(selectedDate)) {
            this.selectedDate = selectedDate;
            this.dateControl?.setValue(this.selectedDate);
            this.setStartDate(selectedDate);
            this.selectDate(selectedDate, false);
        } else {
            this.resetSelectedDate();
            this.resetSelectedTime;
        }
    }

    // Set startDate for datepicker to make sure that the selected date is shown on load
    setStartDate(date: any) {
        this.startDate = {
            year: date.year,
            month: date.month,
            day: date.day
        };
    }

    // Format dates with rentals, and merge with existing available dates
    formatAndMergeDates(datesWithRentals: any) {
        if (datesWithRentals.length) {
            if (this.formattedAvailableShopDates) {
                this.formattedAvailableShopDatesWithRental = this.constructDatePickerObjects(datesWithRentals, true);
                const updatedDatesArray = this.formattedAvailableShopDates.map(date => this.formattedAvailableShopDatesWithRental.find(rentalDate => rentalDate.id === date.id) || date);
                return updatedDatesArray;
            } else {
                return this.formattedAvailableShopDates;
            }
        } else {
            return this.formattedAvailableShopDates;
        }
    }

    selectRentalCar(rentalCar: any) {
        this.isLoadingDates = true;
        this.selectedRental = rentalCar;

        this.resetSelectedDate();
        this.resetSelectedTime();

        if (rentalCar === undefined) {
            this.allDates = this.formattedAvailableShopDates;
            this.isLoadingDates = false;
            return;
        }

        const rentalCarId = this.selectedRental.carClassId;

        // Re-fetch dates with rentals for specific car type
        this.workshopService.getBookingDatesWithRental(this.booking, rentalCarId)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((datesWithRentals: any) => {
                if (datesWithRentals) {
                    this.allDates = this.formatAndMergeDates(datesWithRentals);
                } else {
                    this.allDates = this.formattedAvailableShopDates;
                }

                this.isLoadingDates = false;
                this.cdr.markForCheck();
            });
    }

    selectDate(date: any, resetTime?: boolean) {
        // Make sure dates not in our available dates array can not be selected
        if (!this.dateIsAvailable(date)) {
            return;
        }

        this.resetSelectedTime();
        this.availableTimes = [];
        this.availableTimesSections = undefined;
        this.hasSelectedDate = true;
        this.selectedDate = date;
        this.isLoadingTimes = true;

        if (this.selectedDate) {
            const selectedDate = `${this.selectedDate.year}-${this.selectedDate.month}-${this.selectedDate.day}T00:00:00`;

            this.selectedDateString = getUTCDateString({
                year: this.selectedDate.year,
                month: this.selectedDate.month,
                day: this.selectedDate.day,
                time: '00:00'
            });

            this.dateControl?.setValue(selectedDate);

            // Get available booking times
            this.workshopService.getBookingTimes(this.booking, selectedDate)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: (times: any) => {
                        if (times) {
                            this.availableTimes = times;

                            this.availableTimesSections = {
                                morning: [],
                                beforenoon: [],
                                afternoon: []
                            };

                            for (const time of this.availableTimes) {
                                const timeSplit = Number(time.split(':')[0]);

                                if (timeSplit < 10) {
                                    this.availableTimesSections.morning.push(time);
                                } else if (timeSplit < 12) {
                                    this.availableTimesSections.beforenoon.push(time);
                                } else {
                                    this.availableTimesSections.afternoon.push(time);
                                }
                            }

                            if (this.booking && this.booking.timeCode && !resetTime) {
                                // Make sure that selected time is still valid
                                const selectedTimeStillValid = !!times.find((time: string) => time === this.booking.timeCode);

                                if (selectedTimeStillValid) {
                                    this.selectedTime = this.booking.timeCode;
                                    this.timeControl?.setValue(this.selectedTime);
                                } else {
                                    this.selectedTime = '';
                                    this.timeControl?.setValue('');
                                }

                                if (this.selectedDate && this.selectedTime) {
                                    this.canSubmit = true;
                                }
                            }

                            this.isLoadingTimes = false;
                            this.cdr.markForCheck();
                        } else {
                            this.isLoadingTimes = false;
                            console.warn('No times found for date');
                        }
                    },
                    error: (error: any) => {
                        console.warn('[BN error: No times gotten]', error);
                    },
                    complete: () => {
                        this.isLoadingTimes = false;
                    }
                });
        }

        this.cdr.markForCheck();

    }

    selectTime(time: string) {
        this.selectedTime = time;
        this.timeControl?.setValue(time);
        this.canSubmit = this.dateControl?.value && this.timeControl?.value ? true : false;
    }

    resetSelectedDate() {
        this.dateControl?.setValue('');
        this.selectedDate = undefined;
        this.canSubmit = false;
        this.cdr.markForCheck();
    }

    resetSelectedTime() {
        this.timeControl?.setValue('');
        this.selectedTime = '';
        this.canSubmit = false;
        this.availableTimesSections = undefined;
        this.cdr.markForCheck();
    }

    constructDatePickerObjects(dates: any[], withRental?: boolean): any[] {
        return dates.map((date: string) => {
            const normalizedDate = this.normalizeDateTime(date);
            const splitDate = normalizedDate.split('-');

            if (withRental) {
                return {
                    id: date,
                    year: Number(splitDate[0]),
                    month: Number(splitDate[1]),
                    day: Number(splitDate[2]),
                    isAvailable: true,
                    isAvailableWithRental: true
                }
            } else {
                return {
                    id: date,
                    year: Number(splitDate[0]),
                    month: Number(splitDate[1]),
                    day: Number(splitDate[2]),
                    isAvailable: true
                }
            }

        });
    }

    dateIsAvailable(calendarDate: any) {
        for (const date of this.allDates) {
            if (calendarDate.year === date.year && calendarDate.month === date.month && calendarDate.day === date.day) {
                return date.isAvailable;
            }
        }

        this.cdr.markForCheck();
    }

    dateIsAvailableWithRental(calendarDate: any) {
        for (const date of this.allDates) {
            if (calendarDate.year === date.year && calendarDate.month === date.month && calendarDate.day === date.day) {
                return date.isAvailableWithRental;
            }
        }

        this.cdr.markForCheck();
    }

    private displayNoRentalAvailable(show: boolean) {
        this.showNoRentalAvailableOverlay = show;
        this.cdr.markForCheck();
    }

    private normalizeDateTime(date: string) {
        // working with only this portion of the date/time string: "2020-12-15T00:00:00"
        return date.split('T')[0];
    }

    onSubmit() {
        if (this.selectedRental && !this.dateIsAvailableWithRental(this.selectedDate)) {
            this.displayNoRentalAvailable(true);
            return;
        }

        if (this.form.invalid || !this.timeControl?.value) {
            this.form.markAllAsTouched();
            return;
        } else {
            this.goToNextStep();
        }
    }

    continueWithNoRentalOnDate(continueWithNoRental: boolean) {
        if (continueWithNoRental) {
            this.selectedRental = undefined;
            this.goToNextStep();
        } else {
            this.displayNoRentalAvailable(false);
        }
    }

    goToNextStep() {
        this.nextStep.emit({
            date: this.dateControl?.value,
            timeCode: this.timeControl?.value,
            rentalCar: this.selectedRental ? true : false,
            rentalCarClassName: this.selectedRental ? this.selectedRental.name : ''
        });
    }
}
